<script setup lang="ts">
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { IconImage, Tooltip, Menu } from '@/shared/ui';
  import { LogoutButton } from '@/features/Auth';
  import DocumentManagerIcon from '@/shared/assets/icons/file-text-multiple.svg';
  import { useBranchLogo, SelectBranchInput } from '@/entities/Branch';
  import { useUserStore } from '@/entities/User';
  import { MenuItem } from '@/shared/lib';
  import type { CaslAction, CaslSubject } from '@/shared/lib/casl';

  interface Props {
    isCollapsed: boolean;
    userName: string;
    userEmail: string;
    adminMenuItems: MenuItem[];
    canCheck?: (action: CaslAction, subject: CaslSubject) => boolean;
  }

  const props = defineProps<Props>();

  const router = useRouter();
  const userStore = useUserStore();
  const { currentLogoUrl, handleLogoError } = useBranchLogo();

  const showDocumentManager = computed(
    () => !userStore.isSuperuser || userStore.isMemberOfSelectedBranch
  );

  const handleDocumentManager = () => {
    router.push('/');
  };

  const emit = defineEmits<{
    expand: [];
  }>();

  const handleExpand = () => {
    emit('expand');
  };
</script>

<template>
  <div class="flex flex-col h-full w-full min-h-180">
    <div :class="[props.isCollapsed ? 'my-5' : 'py-8 pl-5 pr-3.5']" class="flex-1">
      <div :class="[props.isCollapsed ? 'flex flex-col items-center' : '']">
        <router-link
          v-if="showDocumentManager"
          to="/"
          data-testid="sidebar-logo-link"
          aria-label="Ir para a página inicial"
          class="inline-block"
        >
          <img
            :class="[props.isCollapsed ? 'w-14 mb-4' : 'w-26 my-2.5']"
            :src="currentLogoUrl"
            alt="Logo HTM"
            @error="handleLogoError"
          />
        </router-link>
        <img
          v-else
          :class="[props.isCollapsed ? 'w-14 mb-4' : 'w-26 my-2.5']"
          :src="currentLogoUrl"
          alt="Logo HTM"
          @error="handleLogoError"
        />
        <p
          v-if="!props.isCollapsed"
          class="text-[var(--theme-text)] text-xs font-semibold text-left mt-3"
        >
          Gestor de Documentos 2.0
        </p>
        <p v-else class="flex flex-col items-center">...</p>
      </div>

      <Menu
        :is-collapsed="props.isCollapsed"
        :menu-items="adminMenuItems"
        :can-check="props.canCheck"
        menu-label="MENU"
      />
    </div>

    <div
      :class="[
        props.isCollapsed
          ? 'mb-8 px-5 w-full flex flex-col items-center gap-2 pb-6'
          : 'mb-8 px-5 w-full flex flex-col items-start gap-2 pb-6',
      ]"
    >
      <template v-if="showDocumentManager">
        <button
          v-if="!props.isCollapsed"
          class="bg-[var(--theme-secondary)] w-46 text-white flex items-center gap-3 h-11 mr-1.5 py-2 px-3 rounded-lg justify-start"
          @click="handleDocumentManager"
        >
          <IconImage :src="DocumentManagerIcon" alt="Gestor de Documentos" class="text-white" />
          <span class="text-xs">Gestor de Documentos</span>
        </button>

        <Tooltip v-else content="Gestor de Documentos" placement="right">
          <button
            class="bg-[var(--theme-secondary)] text-white flex items-center gap-3 w-10 h-10 rounded-lg justify-center"
            @click="handleDocumentManager"
          >
            <IconImage :src="DocumentManagerIcon" alt="Gestor de Documentos" class="text-white" />
          </button>
        </Tooltip>
      </template>

      <SelectBranchInput
        :is-collapsed="props.isCollapsed"
        label="Filial"
        button-class="shadow rounded-lg"
        @expand="handleExpand"
      />

      <LogoutButton
        class="mt-2"
        :is-collapsed="props.isCollapsed"
        :user-name="props.userName"
        :user-email="props.userEmail"
      />
    </div>
  </div>
</template>

<style scoped></style>
